/**
 * Add/Edit Role Modal
 *
 * Modal for adding a new role to an event, or editing an existing one when a
 * `role` prop is provided.
 */

import { useEffect, useState } from 'react';
import { createEventRole, updateEventRole } from '../../api/events';
import { getErrorMessage } from '../../api/client';
import { useOrganization } from '../../context/OrganizationContext';
import type { EventRole } from '../../types/models';
import Modal from '../ui/Modal';

/** Convert dollars string to cents for API */
function dollarsToCents(dollars: string): number | null {
  if (!dollars || dollars.trim() === '') return null;
  const parsed = parseFloat(dollars);
  if (isNaN(parsed)) return null;
  return Math.round(parsed * 100);
}

interface AddRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Target event(s). One id = classic single-event add (or edit, with
   * `role`); several = bulk add, applied to each. Never empty.
   */
  eventIds: string[];
  onRoleAdded: () => void;
  /** When provided, the modal opens in edit mode and prefills from this role. */
  role?: EventRole | null;
}

export default function AddRoleModal({
  isOpen,
  onClose,
  eventIds,
  onRoleAdded,
  role,
}: AddRoleModalProps) {
  const { paymentTrackingEnabled } = useOrganization();
  const isEditing = !!role;
  const [roleName, setRoleName] = useState('');
  const [requiredCount, setRequiredCount] = useState(1);
  const [description, setDescription] = useState('');
  // Rate override fields
  const [rateOverride, setRateOverride] = useState('');
  const [rateOverrideType, setRateOverrideType] = useState<'HOURLY' | 'FLAT'>('HOURLY');
  const [showRateOverride, setShowRateOverride] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill / reset fields whenever the modal is opened. Resetting on close is
  // also handled by handleClose; the effect covers reopening on a different role.
  useEffect(() => {
    if (!isOpen) return;
    if (role) {
      setRoleName(role.roleName);
      setRequiredCount(role.requiredCount);
      setDescription(role.description ?? '');
      const hasRate = role.rateOverride !== null && role.rateOverride !== undefined;
      setRateOverride(hasRate ? ((role.rateOverride as number) / 100).toFixed(2) : '');
      setRateOverrideType((role.rateOverrideType as 'HOURLY' | 'FLAT' | null) ?? 'HOURLY');
      setShowRateOverride(hasRate);
    } else {
      setRoleName('');
      setRequiredCount(1);
      setDescription('');
      setRateOverride('');
      setRateOverrideType('HOURLY');
      setShowRateOverride(false);
    }
    setError(null);
  }, [isOpen, role]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roleName.trim()) {
      setError('Role name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const rateInCents = dollarsToCents(rateOverride);
      // Send rate fields when payment tracking is enabled. When the override
      // panel is collapsed/cleared, send null to clear any existing override.
      const ratePayload =
        paymentTrackingEnabled
          ? showRateOverride && rateInCents !== null
            ? { rateOverride: rateInCents, rateOverrideType }
            : { rateOverride: null, rateOverrideType: null }
          : {};

      if (isEditing && role) {
        await updateEventRole(eventIds[0], role.id, {
          roleName: roleName.trim(),
          requiredCount,
          description: description.trim() || null,
          ...ratePayload,
        });
      } else {
        const targets = eventIds;
        const input = {
          roleName: roleName.trim(),
          requiredCount,
          description: description.trim() || null,
          ...(paymentTrackingEnabled && showRateOverride && rateInCents !== null && {
            rateOverride: rateInCents,
            rateOverrideType,
          }),
        };
        const results = await Promise.allSettled(
          targets.map((id) => createEventRole(id, input))
        );
        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length > 0) {
          // Leave the modal open so the partial result is impossible to miss.
          const firstError = (failed[0] as PromiseRejectedResult).reason;
          setError(
            `Role added to ${targets.length - failed.length} of ${targets.length} event(s); ` +
              `${failed.length} failed: ${getErrorMessage(firstError)}`
          );
          onRoleAdded();
          return;
        }
      }
      onRoleAdded();
      handleClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRoleName('');
    setRequiredCount(1);
    setDescription('');
    setRateOverride('');
    setRateOverrideType('HOURLY');
    setShowRateOverride(false);
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" labelledBy="add-role-title">
      <form onSubmit={handleSubmit}>
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 id="add-role-title" className="text-lg font-semibold text-gray-900">
            {isEditing
              ? 'Edit Role'
              : eventIds.length > 1
                ? `Add Role to ${eventIds.length} Events`
                : 'Add Role'}
          </h3>
        </div>

        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="roleName" className="block text-sm font-medium text-gray-700 mb-1">
              Role Name *
            </label>
            <input
              id="roleName"
              type="text"
              autoFocus
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
              placeholder="e.g., Camera Operator, Audio Engineer"
            />
          </div>

          <div>
            <label htmlFor="requiredCount" className="block text-sm font-medium text-gray-700 mb-1">
              Number Needed
            </label>
            <input
              id="requiredCount"
              type="number"
              min={1}
              max={50}
              value={requiredCount}
              onChange={(e) => setRequiredCount(parseInt(e.target.value) || 1)}
              className="w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
              placeholder="Brief description of this role..."
            />
          </div>

          {/* Rate Override Section (only when payment tracking enabled) */}
          {paymentTrackingEnabled && (
            <div className="border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={() => setShowRateOverride(!showRateOverride)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <svg
                  className={`w-4 h-4 transition-transform ${showRateOverride ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                Rate Override
              </button>

              {showRateOverride && (
                <div className="mt-3 space-y-3 pl-6">
                  <p className="text-xs text-gray-500">
                    Set a specific rate for this role. Leave blank to use crew member's default rate.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="rateOverride" className="block text-sm font-medium text-gray-700 mb-1">
                        Rate
                      </label>
                      <div className="relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                        <input
                          type="number"
                          id="rateOverride"
                          step="0.01"
                          min="0"
                          value={rateOverride}
                          onChange={(e) => setRateOverride(e.target.value)}
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="rateOverrideType" className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        id="rateOverrideType"
                        value={rateOverrideType}
                        onChange={(e) => setRateOverrideType(e.target.value as 'HOURLY' | 'FLAT')}
                        className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack bg-white"
                      >
                        <option value="HOURLY">Hourly</option>
                        <option value="FLAT">Flat/Daily</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !roleName.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? (isEditing ? 'Saving...' : 'Adding...')
              : (isEditing ? 'Save Changes' : 'Add Role')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
